package org.teddapp.project.ui.skills

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Skill_resource
import org.teddapp.project.resources.ResourceRules
import org.teddapp.project.resources.ResourceRules.State
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddTab
import org.teddapp.project.ui.TeddTabs
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddTagTone
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.projects.MenuAction
import org.teddapp.project.ui.projects.ProjectMoreMenu
import org.teddapp.project.ui.rememberAfterFrame

/**
 * Les ressources de la compétence : ce que l'assistant lit pour répondre. Un lien, un
 * texte ou un fichier ; chacun montre où en est son analyse.
 */
@Composable
internal fun ResourcesSection(model: ResourcesModel, compact: Boolean) {
    DisableSelection {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
            val addButtons: @Composable () -> Unit = {
                TeddButton(text = "Lien", onClick = { model.openAdd(AddMode.Link) }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "link")
                TeddButton(text = "Texte", onClick = { model.openAdd(AddMode.Text) }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "notes")
                if (model.canPickFiles) {
                    TeddButton(text = "Fichier", onClick = { model.openAdd(AddMode.File) }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "plus")
                }
            }
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp), modifier = Modifier.fillMaxWidth()) {
                Text("RESSOURCES", style = TeddType.Overline, color = TeddTokens.InkMuted)
                Text(model.counter, style = TeddType.Mono, color = TeddTokens.InkSubtle, fontSize = 11.5.sp, modifier = Modifier.weight(1f).padding(start = 6.dp))
                if (!compact) addButtons()
            }
            // Sur téléphone, les gestes d'ajout ont leur ligne : à côté du titre, ils se coupaient.
            if (compact) Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) { addButtons() }
            model.notice?.let { Notice(it) { model.notice = null } }
            val items = model.items
            if (items.isEmpty()) {
                Text(
                    "Ajoute un cours, un article ou tes propres notes : l'assistant de la compétence répondra à partir d'eux.",
                    style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 14.sp,
                )
            }
            items.forEachIndexed { index, item ->
                ResourceRow(model, item, first = index == 0, last = index == items.lastIndex, compact = compact)
            }
            if (model.trash.isNotEmpty()) TrashBlock(model)
        }
        model.adding?.let { AddResourceDialog(model, it) }
        model.renaming?.let { RenameDialog(model, it) }
    }
}

@Composable
private fun Notice(text: String, onClose: () -> Unit) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        modifier = Modifier
            .fillMaxWidth()
            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 12.dp, vertical = 10.dp),
    ) {
        Text(text, style = TeddType.Body, color = TeddTokens.Ink, modifier = Modifier.weight(1f))
        Text(
            "Fermer",
            style = TeddType.Caption,
            color = TeddTokens.InkMuted,
            modifier = Modifier.clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = onClose),
        )
    }
}

private fun toneOf(state: State): TeddTagTone = when (state) {
    State.Ready -> TeddTagTone.Success
    State.Refused, State.Failed -> TeddTagTone.Danger
    State.RefreshFailed -> TeddTagTone.Warning
    State.Indexing, State.Uploading -> TeddTagTone.Lime
    State.Pending -> TeddTagTone.Neutral
}

private fun iconOf(r: Skill_resource): String = when {
    r.origin == "link" -> "link"
    r.origin == "text" -> "notes"
    r.format == "image" -> "image"
    else -> "note"
}

@Composable
private fun ResourceRow(model: ResourcesModel, item: ResourceItem, first: Boolean, last: Boolean, compact: Boolean) {
    val r = item.row
    TeddCard(modifier = Modifier.fillMaxWidth(), padding = PaddingValues(horizontal = 14.dp, vertical = 12.dp)) {
        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                TeddIcon(name = iconOf(r), size = 16.dp, color = TeddTokens.InkMuted)
                Text(r.title, style = TeddType.BodyStrong, color = TeddTokens.Ink, maxLines = 2, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f))
                if (!compact) {
                    TeddTag(item.stateLabel, tone = toneOf(item.state), dot = item.state == State.Indexing || item.state == State.Uploading)
                    item.retryLabel?.let { label ->
                        TeddButton(text = label, onClick = { model.reindex(r) }, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm, icon = "refresh")
                    }
                }
                ProjectMoreMenu(
                    listOfNotNull(
                        MenuAction("Renommer", "edit", { model.startRename(r) }),
                        if (!first) MenuAction("Monter", "arrow-up", { model.moveUp(r) }) else null,
                        if (!last) MenuAction("Descendre", "arrow-down", { model.moveDown(r) }) else null,
                        MenuAction("Supprimer", "trash", { model.delete(r) }, danger = true),
                    ),
                    size = 28.dp,
                )
            }
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.padding(start = 26.dp)) {
                if (compact) TeddTag(item.stateLabel, tone = toneOf(item.state), dot = item.state == State.Indexing || item.state == State.Uploading)
                Text(
                    listOfNotNull(item.typeLabel, r.url?.let { ResourceRules.titleForLink(it) }).distinct().joinToString(" · "),
                    style = TeddType.Caption, color = TeddTokens.InkMuted, maxLines = 1, overflow = TextOverflow.Ellipsis,
                )
            }
            item.detail?.let {
                Text(
                    it,
                    style = TeddType.Caption,
                    color = if (item.state == State.Failed || item.state == State.Refused) TeddTokens.Danger else TeddTokens.InkMuted,
                    modifier = Modifier.padding(start = 26.dp),
                )
            }
            // Sur téléphone, l'action vient sous la carte : à côté du titre, elle le coupait.
            if (compact) item.retryLabel?.let { label ->
                TeddButton(
                    text = label, onClick = { model.reindex(r) }, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm, icon = "refresh",
                    modifier = Modifier.padding(start = 26.dp, top = 4.dp),
                )
            }
        }
    }
}

@Composable
private fun TrashBlock(model: ResourcesModel) {
    var open by remember { mutableStateOf(false) }
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(
            (if (open) "▾ " else "▸ ") + "Récemment supprimées · ${model.trash.size}",
            style = TeddType.Caption,
            color = TeddTokens.InkMuted,
            modifier = Modifier.clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { open = !open },
        )
        if (open) model.trash.forEach { r ->
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.padding(start = 12.dp)) {
                TeddIcon(name = iconOf(r), size = 14.dp, color = TeddTokens.InkSubtle)
                Text(r.title, style = TeddType.Body, color = TeddTokens.InkMuted, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f))
                TeddButton(text = "Restaurer", onClick = { model.restore(r) }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "undo")
            }
        }
    }
}

@Composable
private fun DialogFrame(onClose: () -> Unit, content: @Composable () -> Unit) {
    // La fiche vit dans un SelectionContainer : sans DisableSelection, le premier clic
    // dans le dialogue plante le navigateur (voir ConvertToMindMapDialog).
    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        DisableSelection {
            Column(
                modifier = Modifier
                    .widthIn(max = 560.dp)
                    .fillMaxWidth()
                    .padding(16.dp)
                    .heightIn(max = 720.dp)
                    .background(TeddTokens.SurfaceOverlay, RoundedCornerShape(TeddTokens.RLg))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RLg))
                    .verticalScroll(rememberScrollState())
                    .padding(24.dp),
                verticalArrangement = Arrangement.spacedBy(14.dp),
            ) { content() }
        }
    }
}

@Composable
private fun AddResourceDialog(model: ResourcesModel, mode: AddMode) {
    val close = rememberAfterFrame { model.closeAdd() }
    DialogFrame(onClose = close) {
        Text("Ajouter une ressource", style = TeddType.Title2, color = TeddTokens.Ink)
        TeddTabs(
            tabs = listOfNotNull(
                TeddTab(AddMode.Link.name, "Lien", icon = "link"),
                TeddTab(AddMode.Text.name, "Texte", icon = "notes"),
                if (model.canPickFiles) TeddTab(AddMode.File.name, "Fichier", icon = "plus") else null,
            ),
            selected = mode.name,
            onSelect = { name -> AddMode.entries.firstOrNull { it.name == name }?.let(model::openAdd) },
        )
        when (mode) {
            AddMode.Link -> {
                TeddField(label = "Adresse", value = model.url, onValueChange = model::onUrl, placeholder = "https://…")
            }
            AddMode.Text -> {
                TextArea(model.body, model::onBody, "Colle ou écris le texte : un résumé, des notes de cours…")
                Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    TeddButton(text = "Texte simple", onClick = { model.chooseMarkdown(false) }, kind = if (!model.markdown) TeddButtonKind.Secondary else TeddButtonKind.Ghost, size = TeddButtonSize.Sm)
                    TeddButton(text = "Markdown", onClick = { model.chooseMarkdown(true) }, kind = if (model.markdown) TeddButtonKind.Secondary else TeddButtonKind.Ghost, size = TeddButtonSize.Sm)
                }
                Text("${model.body.length} / 100 000 caractères", style = TeddType.Caption, color = TeddTokens.InkSubtle)
            }
            AddMode.File -> {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    TeddButton(text = if (model.picked == null) "Choisir un fichier" else "Changer de fichier", onClick = model::pickFile, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm)
                    model.picked?.let { Text(it.name, style = TeddType.Body, color = TeddTokens.Ink, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f)) }
                }
                Text(
                    "PDF ou Word jusqu'à 20 Mo, image jusqu'à 10 Mo, CSV ou page web jusqu'à 5 Mo, Markdown ou texte jusqu'à 2 Mo.",
                    style = TeddType.Caption, color = TeddTokens.InkMuted,
                )
            }
        }
        TeddField(label = "Titre", value = model.title, onValueChange = model::onTitle, placeholder = "Le nom affiché dans la liste")
        model.formError?.let { Text(it, style = TeddType.Body, color = TeddTokens.Danger) }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            TeddButton(
                text = if (model.busy) "Ajout…" else "Ajouter",
                onClick = model::submit,
                kind = TeddButtonKind.Primary,
                enabled = !model.busy,
                modifier = Modifier.weight(1f),
            )
            TeddButton(text = "Annuler", onClick = close, kind = TeddButtonKind.Ghost)
        }
    }
}

@Composable
private fun RenameDialog(model: ResourcesModel, r: Skill_resource) {
    val close = rememberAfterFrame { model.renaming = null }
    DialogFrame(onClose = close) {
        Text("Renommer la ressource", style = TeddType.Title2, color = TeddTokens.Ink)
        TeddField(label = "Titre", value = model.renameText, onValueChange = { model.renameText = it })
        model.renameError?.let { Text(it, style = TeddType.Body, color = TeddTokens.Danger) }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            TeddButton(text = "Enregistrer", onClick = model::confirmRename, kind = TeddButtonKind.Primary, modifier = Modifier.weight(1f))
            TeddButton(text = "Annuler", onClick = close, kind = TeddButtonKind.Ghost)
        }
    }
}

@Composable
private fun TextArea(value: String, onChange: (String) -> Unit, placeholder: String) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(min = 160.dp, max = 320.dp)
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
            .verticalScroll(rememberScrollState())
            .padding(12.dp),
    ) {
        BasicTextField(
            value = value,
            onValueChange = onChange,
            textStyle = TeddType.Body.copy(color = TeddTokens.Ink, fontSize = 14.sp),
            cursorBrush = SolidColor(TeddTokens.LimeStrong),
            modifier = Modifier.fillMaxWidth(),
            decorationBox = { inner ->
                if (value.isEmpty()) Text(placeholder, color = TeddTokens.InkSubtle, fontSize = 14.sp)
                inner()
            },
        )
    }
}

/**
 * La section seule, sur le fond de l'écran : pour les captures d'écran. [initialAdd]
 * ouvre l'ajout (« Link », « Text » ou « File »), [initialText] y met un texte.
 */
@Composable
fun SkillResourcesPanel(
    repos: org.teddapp.project.db.TeddRepositories,
    skillId: String,
    compact: Boolean,
    initialAdd: String? = null,
    initialText: String? = null,
) {
    val model = rememberResourcesModel(repos.skillResources, skillId, null)
    androidx.compose.runtime.LaunchedEffect(initialAdd) {
        val mode = AddMode.entries.firstOrNull { it.name == initialAdd } ?: return@LaunchedEffect
        model.openAdd(mode)
        initialText?.let(model::onBody)
    }
    Box(Modifier.fillMaxWidth().background(TeddTokens.Bg).padding(if (compact) 16.dp else 32.dp)) {
        ResourcesSection(model, compact)
    }
}
